import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, ExpandMore } from '@mui/icons-material';
import {
  formatForPreBlock,
  getLocalDateKey,
  LOG_DATE_FILTERS,
  oneLinePreview,
} from '../../utils/executionLogUtils';
import { copyTextToClipboard } from '../../utils/clipboard';
import styles from './CompactExecutionLogs.module.scss';

const STATUS_CHIP_CLASS = {
  completed: styles.completedChip,
  running: styles.runningChip,
  failed: styles.failedChip,
  inactive: styles.inactiveChip,
};

const CompactExecutionLogs = ({
  groupedLogs = [],
  loading = false,
  emptyMessage = 'No logs found.',
  filteredEmptyMessage = 'No logs in this range.',
  showDateFilters = false,
  logDateFilter = 'all',
  onLogDateFilterChange,
  customLogDate = '',
  onCustomLogDateChange,
  resetKey,
  resolveLogId = (log, index) => log?.id || `log-${index}`,
  resolveHeader,
  resolveSections,
}) => {
  const [expandedLogIds, setExpandedLogIds] = useState(() => new Set());
  const [expandedSections, setExpandedSections] = useState(() => new Set());
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    setExpandedLogIds(new Set());
    setExpandedSections(new Set());
    setCopiedKey(null);
  }, [resetKey]);

  const totalItems = useMemo(
    () => groupedLogs.reduce((count, group) => count + group.items.length, 0),
    [groupedLogs],
  );

  const toggleLogEntry = (logId) => {
    setExpandedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
        setExpandedSections((sections) => {
          const filtered = new Set(
            [...sections].filter((key) => !String(key).startsWith(`${logId}:`)),
          );
          return filtered;
        });
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const toggleSection = (logId, sectionKey) => {
    const key = `${logId}:${sectionKey}`;
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const copySection = (copyKey, content) => {
    copyTextToClipboard(content)
      .then(() => {
        setCopiedKey(copyKey);
        setTimeout(() => setCopiedKey(null), 1600);
      })
      .catch((error) => {
        console.error('Failed to copy log section:', error);
      });
  };

  const handleCopyClick = (event, copyKey, content) => {
    event.preventDefault();
    event.stopPropagation();
    copySection(copyKey, content);
  };

  if (loading) {
    return (
      <Box className={styles.loadingWrap}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (totalItems === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {showDateFilters && logDateFilter !== 'all' ? filteredEmptyMessage : emptyMessage}
      </Typography>
    );
  }

  return (
    <>
      {showDateFilters && (
        <Box className={styles.logFilters}>
          {LOG_DATE_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              size="small"
              disableRipple
              disableTouchRipple
              className={`${styles.filterPill} ${logDateFilter === filter.value ? styles.filterPillActive : ''}`}
              onClick={() => onLogDateFilterChange?.(filter.value)}
            >
              {filter.label}
            </Button>
          ))}
          {logDateFilter === 'custom' && (
            <TextField
              type="date"
              size="small"
              value={customLogDate}
              onChange={(event) => onCustomLogDateChange?.(event.target.value)}
              className={styles.logDateField}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: getLocalDateKey(new Date()) }}
            />
          )}
        </Box>
      )}

      {groupedLogs.map((group) => (
        <Box key={group.label} className={styles.logGroup}>
          <Typography className={styles.logGroupTitle}>{group.label}</Typography>
          {group.items.map((log, index) => {
            const logId = resolveLogId(log, index);
            const logExpanded = expandedLogIds.has(logId);
            const header = resolveHeader(log);
            const sections = resolveSections(log);
            const statusClassName = STATUS_CHIP_CLASS[header.statusMeta?.tone] || styles.inactiveChip;

            const renderSection = (section) => {
              const content = section.value ? formatForPreBlock(section.value) : '';
              const preview =
                oneLinePreview(section.value, 100) ||
                (section.emptyLabel && !content ? section.emptyLabel : '');
              const sectionExpanded = expandedSections.has(`${logId}:${section.key}`);
              const copyKey = `${logId}:${section.key}`;

              return (
                <Box key={section.key} className={styles.logAccordionSection}>
                  <Box
                    className={styles.logAccordionHeader}
                    onClick={() => toggleSection(logId, section.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') toggleSection(logId, section.key);
                    }}
                  >
                    <Box className={styles.logAccordionTitleWrap}>
                      <span className={styles.logSectionTag}>{section.title}</span>
                      {!sectionExpanded && preview && (
                        <Typography variant="caption" className={styles.sectionPreview}>
                          {preview}
                        </Typography>
                      )}
                    </Box>
                    <Box className={styles.logAccordionActions}>
                      {sectionExpanded && content && (
                        <Tooltip title={copiedKey === copyKey ? 'Copied' : 'Copy'}>
                          <span>
                            <IconButton
                              size="small"
                              className={styles.iconButton}
                              type="button"
                              onMouseDown={(event) => handleCopyClick(event, copyKey, content)}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              aria-label={`Copy ${section.title}`}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <ExpandMore
                        className={styles.expandIcon}
                        sx={{ transform: sectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </Box>
                  </Box>
                  <Collapse in={sectionExpanded} timeout="auto" unmountOnExit>
                    <pre className={styles.logPayload}>{content || section.emptyLabel || '—'}</pre>
                  </Collapse>
                </Box>
              );
            };

            return (
              <Box key={logId} className={styles.logItem}>
                <Box
                  className={styles.logCompactHeader}
                  onClick={() => toggleLogEntry(logId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') toggleLogEntry(logId);
                  }}
                  aria-expanded={logExpanded}
                >
                  <Chip
                    size="small"
                    label={header.statusMeta?.label || 'Unknown'}
                    className={`${styles.statusChip} ${statusClassName}`}
                  />
                  {header.source && (
                    <Chip
                      size="small"
                      label={header.source}
                      variant="outlined"
                      className={styles.sourceChip}
                    />
                  )}
                  <Typography variant="caption" className={styles.logTimeCompact}>
                    {header.time}
                  </Typography>
                  <ExpandMore
                    className={styles.logExpandIcon}
                    sx={{ transform: logExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </Box>

                <Collapse in={logExpanded} timeout="auto" unmountOnExit>
                  <Box className={styles.logDetails}>
                    {header.error && (
                      <Typography variant="body2" className={styles.logError}>
                        {header.error}
                      </Typography>
                    )}
                    {sections.map(renderSection)}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      ))}
    </>
  );
};

export default CompactExecutionLogs;
